import { useEffect, useState } from 'react';
import Brand from './Brand';

const THEME_STORAGE_KEY = 'volleywire.theme.v1';

const PRIMARY_ITEMS = [
  { id: 'scores', label: 'Scores' },
  { id: 'teams', label: 'Teams' },
  { id: 'rankings', label: 'Rankings' },
  { id: 'standings', label: 'Standings', mobileLabel: 'Conferences' },
  { id: 'leaders', label: 'Leaders' },
];

const ROOT_FEATURE_VIEWS = new Set(['rankings', 'standings', 'leaders']);

function MobileNavIcon({ view }) {
  const common = {
    width: 21, height: 21, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round',
    strokeLinejoin: 'round', focusable: 'false',
  };
  if (view === 'scores') return <svg {...common}><path d="M4 5h16v14H4z"/><path d="M8 9h3M8 13h3M15 9h1M15 13h1"/></svg>;
  if (view === 'teams') return <svg {...common}><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3.5 19c.7-3.3 2.5-5 5.5-5s4.8 1.7 5.5 5M15 14c2.7 0 4.3 1.4 5 4"/></svg>;
  if (view === 'rankings') return <svg {...common}><path d="M7 4h10v4c0 3.3-2 5.5-5 5.5S7 11.3 7 8V4Z"/><path d="M7 6H4v1c0 2 1.3 3.5 3.5 3.5M17 6h3v1c0 2-1.3 3.5-3.5 3.5M12 13.5V17M8.5 20h7M10 17h4"/></svg>;
  if (view === 'standings') return <svg {...common}><path d="M5 6h14M5 12h14M5 18h14"/><path d="M8 4v4M14 10v4M11 16v4"/></svg>;
  return <svg {...common}><path d="M5 19V9M12 19V5M19 19v-7"/><path d="m9 8 3-3 3 3"/></svg>;
}

function readInitialTheme() {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);

    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // Dark remains the default when storage is unavailable.
  }

  return 'dark';
}

function shouldReduceMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function dispatchNavigationEvent() {
  window.dispatchEvent(new Event('volleywire:navigation'));
  window.dispatchEvent(
    typeof PopStateEvent === 'function'
      ? new PopStateEvent('popstate')
      : new Event('popstate')
  );
}

function buildViewUrl(view) {
  const url = new URL(window.location.href);
  url.hash = view;
  url.searchParams.delete('match');
  url.searchParams.delete('sport');

  if (view === 'scores') {
    url.searchParams.delete('team');
    url.searchParams.delete('teamSeason');
  }

  if (ROOT_FEATURE_VIEWS.has(view)) {
    url.searchParams.delete('team');
    url.searchParams.delete('teamSeason');
  }

  if (view !== 'rankings') {
    url.searchParams.delete('rankingSeason');
  }

  if (view !== 'standings') {
    url.searchParams.delete('standingsConference');
  }

  if (view !== 'leaders') {
    url.searchParams.delete('leadersType');
    url.searchParams.delete('leadersCategory');
  }

  if (view === 'scores' || view === 'teams') {
    url.searchParams.delete('rankingSeason');
    url.searchParams.delete('standingsConference');
    url.searchParams.delete('leadersType');
    url.searchParams.delete('leadersCategory');
  }

  return url;
}

function navigateToRootFeature(view) {
  const url = buildViewUrl(view);

  // Feature pages are separate root shells. A real navigation prevents iOS
  // Safari from retaining the currently mounted App view after pushState.
  window.location.assign(url.toString());
}

function fallbackNavigate(view) {
  const url = buildViewUrl(view);

  window.history.pushState({}, '', url.toString());
  dispatchNavigationEvent();

  window.requestAnimationFrame(() => {
    document.getElementById(view)?.scrollIntoView({
      behavior: shouldReduceMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  });
}

export default function Header({
  activeView = 'scores',
  onNavigate,
}) {
  const [theme, setTheme] = useState(readInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.colorScheme = theme;

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Theme remains active for the current page.
    }
  }, [theme]);

  function toggleTheme() {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }

  function navigateTo(view) {
    if (ROOT_FEATURE_VIEWS.has(view)) {
      if (activeView === view) {
        document.getElementById(view)?.scrollIntoView({
          behavior: shouldReduceMotion() ? 'auto' : 'smooth',
          block: 'start',
        });
      } else {
        navigateToRootFeature(view);
      }
      return;
    }

    if (typeof onNavigate === 'function') {
      onNavigate(view);
      return;
    }

    fallbackNavigate(view);
  }

  function handlePrimaryClick(event, view) {
    event.preventDefault();
    navigateTo(view);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }));
  }

  const nextThemeLabel =
    theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <>
      <header className="site-header">
        <div className="shell header-inner">
        <Brand />

        <nav
          className="primary-nav primary-nav-desktop"
          aria-label="Primary navigation"
        >
          {PRIMARY_ITEMS.map((item) => {
            const active = activeView === item.id;

            return (
              <a
                key={item.id}
                className={active ? 'active' : ''}
                href={`#${item.id}`}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => handlePrimaryClick(event, item.id)}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="header-actions">
          <button
            className="theme-toggle"
            type="button"
            onClick={toggleTheme}
            aria-label={nextThemeLabel}
            title={nextThemeLabel}
            aria-pressed={theme === 'light'}
          >
            <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          </button>
        </div>
        </div>
      </header>

      <nav className="mobile-tab-bar" aria-label="Primary mobile navigation">
        {PRIMARY_ITEMS.map((item) => {
          const active = activeView === item.id;
          return (
            <a
              key={item.id}
              className={active ? 'active' : ''}
              href={`#${item.id}`}
              aria-current={active ? 'page' : undefined}
              onClick={(event) => handlePrimaryClick(event, item.id)}
            >
              <span className="mobile-tab-icon" aria-hidden="true">
                <MobileNavIcon view={item.id} />
              </span>
              <span>{item.mobileLabel || item.label}</span>
            </a>
          );
        })}
      </nav>
    </>
  );
}
